<template>
  <div class="orders-grid-actions">
    <BackstageTooltip :text="tooltipText" :key="seconds" v-if="showDraftAction">
      <BackstageIcon name="edit" class="orders-grid-actions-edit-draft" @click="onDraftEdit" />
    </BackstageTooltip>
    <BackstageIcon name="xlsx-file" class="orders-grid-actions-xlsx-download" @click="onXlsxDownload(props.params)" />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/stores/user';
import useCounter from '@/composables/useCounter';
import { computed, getCurrentInstance } from 'vue';
import { useAnalytics } from '@/composables/analytics';
import { downloadXlsxFile } from '@/composables/download';
import { OrdersService } from '@/services/orders.service';
import { OrderStatus } from '@onrunning/backstage-models';
import { GaCustomEvents } from '@/models/ga-custom-events.enum';
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import BackstageTooltip from '@/components/common/BackstageTooltip.vue';

const props = defineProps<{
  params: any; // Provided by ag-grid // TODO : eventually should add type for params
}>();

const { selectedAccountId: accountId } = storeToRefs(useUserStore());

const { t } = useI18n();
const { addCustomGaEvent } = useAnalytics();

const globalProperties = getCurrentInstance()?.appContext.config.globalProperties;

const onXlsxDownload = async (params: any) => {
  const isDraft = params.data.status === OrderStatus.NotSubmitted;

  const ordersService = new OrdersService();
  if (params.colDef.field === 'actions') {
    ordersService.downloadOrder(params.data.id, accountId.value as string, isDraft).then((response) => {
      if (response.isValid()) {
        try {
          downloadXlsxFile(response.model.base64Body, `order-${params.data.orderNumber}`);
        } catch (err) {
          addCustomGaEvent({
            event: GaCustomEvents.FileDownloadError,
            link_text: params.data.orderNumber,
          });
          globalProperties?.$snotify.warning(t('documentsCheckout.document_is_not_available'));
        }
      } else {
        addCustomGaEvent({
          event: GaCustomEvents.FileDownloadError,
          link_text: params.data.orderNumber,
        });
      }
    });
  }
};

const onDraftEdit = () => {
  // TODO: Will be implemented in upcoming PRs
};

const { minutes, seconds } = useCounter(props.params.data.date);

const tooltipText = computed(() => {
  return t('viewOrders.edit_action_tooltip_title', { time: `${minutes.value}:${seconds.value}` });
});

const isOrdersDraftEditActionEnabled = import.meta.env.VITE_APP_ordersEditAction === 'true';

const showDraftAction = computed(() => {
  return isOrdersDraftEditActionEnabled && (minutes.value > 0 || seconds.value > 0);
});
</script>

<style lang="scss" scoped>
.orders-grid-actions {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  &-xlsx-download {
    width: 14px;
    height: 14px;
    cursor: pointer;
  }

  &-edit-draft {
    width: 18px;
    height: 18px;
    cursor: pointer;
  }
}
</style>
